import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchMyDocuments } from "@/medicalRecord/redux/thunks/extractionThunks";
import { DOCTOR_ROUTES } from "@/routes/patientRoutes/routePaths";
import "./RenalImagingSection.css";

const imagingItems = [
  { id: "catheterization", titleKey: "catheterization", placeholderKey: "catheterReport" },
  { id: "ultrasound", titleKey: "ultrasound", placeholderKey: "ultrasoundReport" },
  { id: "ct-scan", titleKey: "ctScan", placeholderKey: "ctReport" },
  { id: "renal-biopsy", titleKey: "biopsy", placeholderKey: "biopsyReport" },
];

const getDocumentId = (document) =>
  document?.document_id || document?.id_doc || document?._id || document?.id || document?.documentId || "";

const getDocumentName = (document) =>
  document?.nom_fichier || document?.file_name || document?.filename || document?.name || "";

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

export default function RenalImagingSection({
  patientId,
  imagingRecords = [],
  imagingLoading = false,
  imagingError,
}) {
  const { t } = useTranslation("neurologue");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRefs = useRef({});
  const [items, setItems] = useState(imagingItems);
  const [activeItemId, setActiveItemId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState("");

  useEffect(() => {
    const latestByType = new Map();
    imagingRecords.forEach((record) => {
      if (record?.imagingType && !latestByType.has(record.imagingType)) {
        latestByType.set(record.imagingType, record);
      }
    });
    setItems(imagingItems.map((item) => {
      const record = latestByType.get(item.id);
      return {
        ...item,
        date: toDateInputValue(record?.dateDescription),
        report: record?.description || "",
        documentId: record?.documentId || "",
        attachment: record?.documentName || record?.documentId || "",
      };
    }));
  }, [patientId, imagingRecords]);

  useEffect(() => {
    if (!pickerOpen || !patientId) return;
    let active = true;
    setDocuments([]);
    setDocumentsLoading(true);
    setDocumentsError("");
    dispatch(fetchMyDocuments({
      skip: 0,
      limit: 50,
      userId: patientId,
    })).unwrap().then((result) => {
      if (active) setDocuments(result.documents || []);
    }).catch((error) => {
      if (active) setDocumentsError(String(error || t("imaging.documentsLoadFailed")));
    }).finally(() => {
      if (active) setDocumentsLoading(false);
    });
    return () => { active = false; };
  }, [dispatch, patientId, pickerOpen, t]);

  const updateItem = (id, field, value) => {
    setItems((current) => current.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const openPicker = (id) => {
    setActiveItemId(id);
    setPickerOpen(true);
  };

  const selectDocument = (document) => {
    const documentId = getDocumentId(document);
    if (!documentId) {
      setDocumentsError(t("imaging.documentMissingId"));
      return;
    }
    updateItem(activeItemId, "documentId", String(documentId));
    updateItem(activeItemId, "attachment", getDocumentName(document) || String(documentId));
    updateItem(activeItemId, "file", null);
    const fileInput = fileInputRefs.current[activeItemId];
    if (fileInput) fileInput.value = "";
    setPickerOpen(false);
  };

  const selectLocalFile = (event, itemId) => {
    const file = event.target.files?.[0];
    if (file) {
      updateItem(itemId, "file", file);
      updateItem(itemId, "documentId", "");
      updateItem(itemId, "attachment", file.name);
      setPickerOpen(false);
    }
  };

  const openPatientMedicalRecord = () => {
    if (!patientId) return;
    setPickerOpen(false);
    navigate(`${DOCTOR_ROUTES.DOCTOR_PATIENT_VIEW(patientId)}/${DOCTOR_ROUTES.MEDICAL_RECORD_DECOR}`);
  };

  const activeDocumentId = items.find((item) => item.id === activeItemId)?.documentId;
  const availableDocuments = documents.filter((document) => getDocumentId(document));

  return (
    <section className="neurologue-section-card renal-imaging">
      <h3 className="renal-imaging__section-title">{t("imaging.title")}</h3>
      <div className="renal-imaging__list">
        {items.map((item) => {
          const linkedDocument = documents.find((document) =>
            String(getDocumentId(document)) === String(item.documentId)
          );
          const attachmentName = getDocumentName(linkedDocument) || item.attachment;
          return (
            <article className="renal-imaging__card" data-imaging-type={item.id} key={item.id}>
              <div className="renal-imaging__card-header">
                <h4>{t(`imaging.${item.titleKey}`)}</h4>
                <div className="renal-imaging__actions">
                  <label className="renal-imaging__date">
                    <span>{t("imaging.date")}</span>
                    <input
                      type="date"
                      data-imaging-date={item.id}
                      aria-label={t("imaging.dateAria", { name: t(`imaging.${item.titleKey}`) })}
                      value={item.date}
                      onChange={(event) => updateItem(item.id, "date", event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="renal-imaging__attach"
                    aria-label={t("imaging.attachAria", { name: t(`imaging.${item.titleKey}`) })}
                    onClick={() => openPicker(item.id)}
                  >
                    <span aria-hidden="true">↑</span> {t("imaging.attach")}
                  </button>
                </div>
              </div>

              <textarea
                className="renal-imaging__report"
                data-imaging-report={item.id}
                aria-label={t("imaging.reportAria", { name: t(`imaging.${item.titleKey}`) })}
                placeholder={t(`imaging.${item.placeholderKey}`)}
                value={item.report}
                onChange={(event) => updateItem(item.id, "report", event.target.value)}
              />
              <input
                ref={(element) => { fileInputRefs.current[item.id] = element; }}
                className="renal-imaging__local-file-input"
                type="file"
                data-imaging-file
                accept=".pdf,image/*"
                aria-label={t("imaging.fromComputer")}
                onChange={(event) => selectLocalFile(event, item.id)}
              />
              <input type="hidden" data-imaging-document-id value={item.documentId || ""} readOnly />

              {attachmentName && (
                <div className="renal-imaging__file" title={attachmentName}>
                  <span aria-hidden="true">▧</span>
                  <span>{attachmentName}</span>
                </div>
              )}
            </article>
          );
        })}
      </div>
      {imagingLoading && <p role="status">{t("common.loading", { defaultValue: "Chargement des examens d’imagerie…" })}</p>}
      {imagingError && <p role="alert">{String(imagingError)}</p>}

      {pickerOpen && (
        <div className="renal-imaging__modal-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setPickerOpen(false);
        }}>
          <section className="renal-imaging__modal" role="dialog" aria-modal="true" aria-labelledby="renal-imaging-picker-title">
            <div className="renal-imaging__modal-header">
              <h3 id="renal-imaging-picker-title">{t("imaging.pickerTitle")}</h3>
              <button type="button" aria-label={t("imaging.close")} onClick={() => setPickerOpen(false)}>×</button>
            </div>
            <div className="renal-imaging__modal-actions">
              <button type="button" onClick={() => fileInputRefs.current[activeItemId]?.click()}>
                {t("imaging.fromComputer")}
              </button>
            </div>
            <button
              type="button"
              className="renal-imaging__medical-record-link"
              onClick={openPatientMedicalRecord}
              disabled={!patientId}
            >
              {t("imaging.openPatientMedicalRecord")}
            </button>
            <h4>{t("imaging.fromMedicalRecords")}</h4>
            {documentsLoading && <p role="status">{t("imaging.documentsLoading")}</p>}
            {documentsError && <p role="alert">{documentsError}</p>}
            {!documentsLoading && !documentsError && availableDocuments.length === 0 && (
              <p>{t("imaging.noDocuments")}</p>
            )}
            {availableDocuments.length > 0 && (
              <div className="renal-imaging__document-list">
                {availableDocuments.map((document, index) => {
                  const id = String(getDocumentId(document));
                  const name = getDocumentName(document) || id;
                  return (
                    <button
                      type="button"
                      className="renal-imaging__document-option"
                      key={id || index}
                      aria-pressed={id === String(activeDocumentId || "")}
                      onClick={() => selectDocument(document)}
                    >
                      <span aria-hidden="true">▧</span><span>{name}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="renal-imaging__modal-footer">
              <button type="button" onClick={() => setPickerOpen(false)}>{t("imaging.cancel")}</button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
